import { useEffect, useMemo, useState, type ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Minus, Mic, MicOff, PhoneForwarded, PhoneOff, ChevronLeft } from 'lucide-react';
import { useSoftphone } from './SoftphoneProvider';
import { useLeadDetailModal } from '@/contexts/LeadDetailModalContext';
import { CHAVE_AUTO_DIALER, useRetomarLista } from '@/hooks/useAutoDialer';
import { desligarClienteMotor, transferirClienteMotor } from '@/lib/softphoneApi';
import {
  contatoRealAutoDialer, despausarAutoDialer, encerrarAutoDialer, ligarOutroNumeroAutoDialer, naoEDaquiAutoDialer, pausarAutoDialer,
  proximoLeadAutoDialer, proximoNumeroAutoDialer, type EstadoAutoDialer, type NumeroAutoDialer, type StatusNumero,
} from '@/lib/autoDialerApi';

/**
 * AUTO DIALER (Douglas 05–06/10/2026, maquete aprovada).
 *
 * O softphone no modo discagem: em cima os botões da LIGAÇÃO, embaixo os da DISCAGEM —
 * um não mexe no outro. Quem liga é o motor da discadora, pela rota classificada: o
 * consultor fica na sala e não ouve chamar; caixa postal, não atende e número errado
 * são anotados sozinhos e só uma pessoa é entregue. Pausar não derruba a ligação.
 */

/**
 * Campo de digitar dentro do painel com o lead aberto (Douglas 06/10/2026: "pede para colocar
 * nome, mas não me deixa digitar"). O modal do lead prende o foco do teclado dentro dele e
 * devolvia o foco a cada clique no campo. Um escopo de foco próprio por cima pausa essa trava
 * do Radix enquanto o formulário está aberto, sem mexer em nenhum modal do CRM.
 */
function FocoProprio({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <DialogPrimitive.Root open modal={false}>
      <DialogPrimitive.Content
        aria-describedby={undefined}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogPrimitive.Title className="sr-only">{titulo}</DialogPrimitive.Title>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Root>
  );
}

const BTN = 'h-8 rounded-lg border border-border bg-card px-2 text-[11px] font-semibold text-foreground transition-colors hover:bg-[#F4F0EA] disabled:cursor-not-allowed disabled:text-muted-foreground dark:hover:bg-[#2A1F1F]';
const BTN_PRIMARIO = 'h-10 rounded-[10px] border-0 bg-[linear-gradient(150deg,#4A1320,#5B0A0D)] px-3 text-[12.5px] font-bold text-white transition hover:brightness-110 disabled:opacity-60 dark:bg-[linear-gradient(150deg,#9D2530,#6C2236)]';
const BTN_BORDO = 'h-9 rounded-[9px] border border-[#5B0A0D] bg-card px-2 text-[11.5px] font-bold text-[#5B0A0D] hover:bg-[#F6ECEC] dark:border-[#C87484] dark:text-[#C87484] dark:hover:bg-[#2A1F1F]';

const diaHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
const fmt = (t: string) => (t.length === 11 ? `(${t.slice(0, 2)}) ${t.slice(2, 7)}-${t.slice(7)}` : t);
const brl = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const mmss = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

function operadora(o: string | null): string | null {
  const u = String(o || '').toUpperCase();
  if (!u) return null;
  if (u.startsWith('CLARO')) return 'Claro';
  if (u.startsWith('VIVO') || u.startsWith('TELEF')) return 'Vivo';
  if (u.startsWith('TIM')) return 'TIM';
  if (u.startsWith('OI')) return 'Oi';
  return u.charAt(0) + u.slice(1).toLowerCase();
}

const ROTULO_STATUS: Record<StatusNumero, string> = {
  na_fila: 'na fila', discando: 'procurando', atendeu: 'atendeu', caixa_postal: 'caixa postal', nao_atende: 'não atendeu',
  ocupado: 'ocupado', invalido: 'número inválido', erro: 'falhou', quarentena: 'quarentena', nao_e_daqui: 'não é daqui', pulado: 'pulado',
};

function corDoPonto(s: StatusNumero): string {
  if (s === 'atendeu') return 'bg-[#075E54] dark:bg-[#25D366]';
  if (s === 'discando') return 'bg-[#5B0A0D] shadow-[0_0_0_3px_rgba(91,10,13,0.16)] dark:bg-[#C87484]';
  if (s === 'na_fila') return 'border-[1.5px] border-[#A6907B] bg-transparent';
  if (s === 'quarentena' || s === 'nao_e_daqui' || s === 'pulado') return 'bg-[#E4DDD3] dark:bg-[#332727]';
  return 'bg-[#B4808C]';
}

function corDaBarra(s: StatusNumero): string {
  if (s === 'atendeu') return 'bg-[#25D366]';
  if (s === 'discando') return 'bg-white';
  if (s === 'na_fila') return 'bg-white/20';
  return 'bg-[#B4808C]';
}

export function AutoDialerPainel({ estado, onFechar }: { estado: EstadoAutoDialer; onFechar: () => void }) {
  const sp = useSoftphone();
  const qc = useQueryClient();
  const { open: abrirLead } = useLeadDetailModal();
  const [recolhido, setRecolhido] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [contatoDe, setContatoDe] = useState<NumeroAutoDialer | null>(null);
  const [nome, setNome] = useState('');
  const [cargo, setCargo] = useState('');
  const [transferindo, setTransferindo] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [outroAberto, setOutroAberto] = useState(false);
  const [outroTel, setOutroTel] = useState('');
  const retomar = useRetomarLista();
  const [, setTick] = useState(0);

  const s = estado.sessao;
  const st = s.status;
  const lead = estado.lead_atual;
  const numeros = estado.numeros;
  const atual = numeros.find((n) => n.id === s.numero_atual) ?? numeros.find((n) => n.status === 'discando') ?? null;
  const posAtual = atual ? numeros.indexOf(atual) + 1 : 0;
  const naFila = numeros.filter((n) => n.status === 'na_fila').length;
  const proximo = estado.proximos[0] ?? null;

  useEffect(() => {
    if (st !== 'falando') return;
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, [st]);
  useEffect(() => { if (st !== 'falando') { setTransferindo(false); } }, [st]);
  const { ramais: ramaisSp, carregarRamais } = sp;
  useEffect(() => { if (transferindo && !ramaisSp) carregarRamais(); }, [transferindo, ramaisSp, carregarRamais]);

  const agir = async (fn: () => Promise<unknown>, erro: string) => {
    setOcupado(true);
    try { await fn(); await qc.invalidateQueries({ queryKey: CHAVE_AUTO_DIALER }); }
    catch (e) { toast.error(erro, { description: (e as Error).message }); }
    finally { setOcupado(false); }
  };

  const falandoHa = st === 'falando' && atual?.resultado_em ? Math.max(0, Math.floor((Date.now() - new Date(atual.resultado_em).getTime()) / 1000)) : 0;

  const ramaisFiltrados = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    return (sp.ramais || []).filter((r) => !q || `${r.nome} ${r.ramal}`.toLowerCase().includes(q)).slice(0, 30);
  }, [sp.ramais, filtro]);

  const statusPill = st === 'falando' ? 'Em ligação' : st === 'procurando' ? 'Procurando' : st === 'esperando' ? 'Esperando você'
    : st === 'pausado' ? 'Pausado' : st === 'abrindo' ? 'Abrindo a sala' : 'Encerrado';
  const dotPill = st === 'falando' ? 'bg-[#25D366]' : st === 'pausado' ? 'bg-[#E88A8A]' : 'bg-white';

  if (recolhido && st !== 'encerrada') {
    return (
      <button
        type="button"
        data-softphone-ui="true"
        onClick={() => setRecolhido(false)}
        className="pointer-events-auto fixed bottom-[152px] right-6 z-[2147483000] flex h-10 items-center gap-2 rounded-full bg-[linear-gradient(150deg,#5E1424,#3E0E1B)] px-4 text-[12px] font-bold text-white shadow-[0_10px_30px_-8px_rgba(30,10,15,0.6)]"
        aria-label="Abrir o Auto Dialer"
      >
        <span aria-hidden className={`h-2 w-2 rounded-full ${dotPill}`} />
        Auto Dialer · {statusPill}{atual && st !== 'pausado' ? ` · ${posAtual}/${numeros.length}` : ''}
      </button>
    );
  }

  return (
    <div
      data-softphone-ui="true"
      className="pointer-events-auto fixed bottom-[152px] right-6 z-[2147483000] flex max-h-[calc(100vh-180px)] w-[372px] flex-col overflow-hidden rounded-[18px] border border-border bg-card font-sans text-foreground shadow-[0_22px_54px_-14px_rgba(30,10,15,0.7)]"
    >
      {/* cabeçalho */}
      <div className="flex-none bg-[linear-gradient(150deg,#5E1424,#3E0E1B)] px-3.5 py-3 text-white">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-extrabold">Auto Dialer</span>
          <span className="inline-flex h-5 items-center gap-1.5 rounded-full bg-white/[0.12] px-2 text-[10px] font-bold">
            <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${dotPill}`} />{statusPill}
          </span>
          <span className="flex-1" />
          <span className="text-[10.5px] text-white/75">lead {Math.min(estado.totais.feitos + 1, estado.totais.leads)} de {estado.totais.leads}</span>
          {st !== 'encerrada' && (
            <button type="button" onClick={() => setRecolhido(true)} aria-label="Recolher" title="Recolher"
              className="flex h-[26px] w-[26px] items-center justify-center rounded-[7px] border border-white/[0.13] bg-white/10">
              <Minus className="h-3 w-3" />
            </button>
          )}
        </div>
        {numeros.length > 0 && (
          <div className="mt-2 grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${numeros.length}, minmax(0, 1fr))` }}>
            {numeros.map((n) => <div key={n.id} className={`h-[5px] rounded-[3px] ${corDaBarra(n.status)}`} />)}
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* o lead e a proposta pronta */}
        {lead && st !== 'encerrada' && (
          <div className="flex items-center gap-2.5 border-b border-border px-3.5 py-2.5">
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12.5px] font-bold" title={lead.razao_social || ''}>{lead.razao_social || 'Lead'}</div>
              <div className="truncate text-[10.5px] text-muted-foreground">
                {[lead.tipo_lead, lead.top ? `TOP ${lead.top}` : null].filter(Boolean).join(' · ')}
                {lead.previa && !lead.previa.pedir_conta && lead.previa.total_3m != null && lead.previa.hoje?.valor != null && (
                  <> · hoje <b className="text-foreground">{brl(lead.previa.hoje.valor)}</b> → <b className="text-[#5B0A0D] dark:text-[#C87484]">{brl(lead.previa.total_3m)}</b></>
                )}
                {lead.previa?.pedir_conta && <> · sem a conta: peça a conta</>}
              </div>
              {lead.previa && !lead.previa.pedir_conta && lead.previa.total_3m != null && lead.previa.hoje?.valor == null && (
                <div className="truncate text-[10.5px] text-muted-foreground" title={lead.previa.resumo_claro || ''}>
                  {lead.previa.resumo_claro || 'Proposta'}: <b className="text-[#5B0A0D] dark:text-[#C87484]">{brl(lead.previa.total_3m)}</b>
                </div>
              )}
              {lead.email?.enviado_em && (
                <div className="truncate text-[10.5px] text-muted-foreground" title={lead.email.modelo || ''}>
                  E-mail {diaHora(lead.email.enviado_em)}
                  {lead.email.clicou_em
                    ? <> · <b className="text-[#5B0A0D] dark:text-[#C87484]">clicou {diaHora(lead.email.clicou_em)}</b></>
                    : lead.email.abriu ? <> · <b className="text-foreground">abriu{lead.email.abriu_em ? ` ${diaHora(lead.email.abriu_em)}` : ''}</b></> : <> · não abriu</>}
                </div>
              )}
            </div>
            <button type="button" onClick={() => abrirLead(lead.lead_id, 'proposta')} className={`${BTN_BORDO} h-[30px] flex-none px-2.5 text-[11px]`}>Abrir o lead</button>
          </div>
        )}

        {/* a ligação */}
        <div className="border-b border-border px-3.5 py-3">
          {sp.incoming && st !== 'abrindo' && (
            <div className="mb-2.5 space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-[1.3px] text-[#5B0A0D] dark:text-[#C87484]">A sala está chamando</div>
              <button type="button" onClick={sp.answerCall} className={`${BTN_PRIMARIO} w-full`}>Entrar na sala</button>
            </div>
          )}
          {st === 'abrindo' && (
            <div className="space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-[1.3px] text-[#5B0A0D] dark:text-[#C87484]">Abrindo a sala</div>
              {sp.status !== 'registrado' ? (
                <p className="text-[12px] text-[#7A2235] dark:text-[#C87484]">O seu softphone está desconectado. Conecte o softphone para a sala abrir.</p>
              ) : sp.incoming ? (
                <button type="button" onClick={sp.answerCall} className={`${BTN_PRIMARIO} w-full`}>Entrar na sala</button>
              ) : (
                <p className="text-[12px] text-muted-foreground">O softphone vai tocar uma vez. Atenda para entrar na sala; daí em diante você só ouve quando uma pessoa atender.</p>
              )}
              {lead && (lead.status === 'preparando' || lead.status === 'montando') && (
                <p className="text-[11px] text-muted-foreground">{lead.assertiva === 'pedida' || lead.assertiva === 'socios' ? 'Procurando mais números na Assertiva…' : 'Preparando os números do lead…'}</p>
              )}
            </div>
          )}

          {st === 'procurando' && (
            <div className="space-y-1.5">
              <div className="text-[10px] font-bold uppercase tracking-[1.3px] text-[#5B0A0D] dark:text-[#C87484]">
                {atual?.status === 'discando' ? `Procurando · número ${posAtual} de ${numeros.length}` : lead && (lead.status === 'preparando' || lead.status === 'montando') ? 'Preparando os números' : 'Procurando o próximo número'}
              </div>
              {atual?.status === 'discando' && (
                <>
                  <div className="font-display text-[24px] font-extrabold leading-none tabular-nums">{fmt(atual.telefone)}</div>
                  <div className="text-[11px] text-muted-foreground">{[atual.rotulo, operadora(atual.operadora), atual.whatsapp ? 'WhatsApp' : null].filter(Boolean).join(' · ')}</div>
                </>
              )}
              {lead && (lead.assertiva === 'pedida' || lead.assertiva === 'socios') && (
                <p className="text-[11px] text-muted-foreground">Procurando mais números na Assertiva…</p>
              )}
              <p className="rounded-[9px] bg-[#F4F0EA] px-2.5 py-2 text-[11px] leading-snug text-muted-foreground dark:bg-[#2A1F1F]">
                Você está na sala, sem ouvir chamar. Caixa postal, não atende e número errado são anotados sozinhos.
              </p>
            </div>
          )}

          {st === 'falando' && atual && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[1.3px] text-[#075E54] dark:text-[#25D366]">
                <span>Em ligação</span><span className="font-display text-[13px] tracking-normal text-foreground">{mmss(falandoHa)}</span>
              </div>
              <div className="font-display text-[24px] font-extrabold leading-none tabular-nums">{fmt(atual.telefone)}</div>
              <div className="text-[11px] text-muted-foreground">{[atual.rotulo, operadora(atual.operadora), atual.whatsapp ? 'WhatsApp' : null].filter(Boolean).join(' · ')}</div>

              {transferindo ? (
                <FocoProprio titulo="Transferir a ligação">
                <div className="space-y-1.5 rounded-[10px] border border-border p-2">
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setTransferindo(false)} aria-label="Voltar" className="flex h-7 w-7 items-center justify-center rounded-md border border-border"><ChevronLeft className="h-3.5 w-3.5" /></button>
                    <input value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="transferir para…" className="h-7 min-w-0 flex-1 rounded-md border border-border bg-card px-2 text-[12px]" />
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    {ramaisFiltrados.map((r) => (
                      <button key={r.ramal} type="button" disabled={ocupado}
                        onClick={() => agir(async () => {
                          const res = await transferirClienteMotor(r.ramal, s.call_id);
                          if (!res.ok) throw new Error(res.motorForaDoAr ? 'O motor da discadora está fora do ar.' : 'A transferência não foi feita.');
                          setTransferindo(false);
                        }, 'Não transferiu')}
                        className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-[12px] hover:bg-[#F4F0EA] dark:hover:bg-[#2A1F1F]">
                        <span className="truncate">{r.nome}</span><span className="text-[11px] font-semibold text-[#5B0A0D] dark:text-[#C87484]">{r.ramal}</span>
                      </button>
                    ))}
                  </div>
                </div>
                </FocoProprio>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  <button type="button" onClick={sp.toggleMute} className={`${BTN} flex h-[54px] flex-col items-center justify-center gap-1 ${sp.muted ? 'border-[#5B0A0D] text-[#5B0A0D] dark:text-[#C87484]' : ''}`}>
                    {sp.muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}{sp.muted ? 'Sem som' : 'Mudo'}
                  </button>
                  <button type="button" onClick={() => setTransferindo(true)} className={`${BTN} flex h-[54px] flex-col items-center justify-center gap-1`}>
                    <PhoneForwarded className="h-4 w-4" />Transferir
                  </button>
                  <button type="button" disabled={ocupado}
                    onClick={() => agir(async () => {
                      const res = await desligarClienteMotor(s.call_id);
                      if (!res.ok) throw new Error(res.motorForaDoAr ? 'O motor da discadora está fora do ar.' : 'O pedido não chegou ao motor.');
                    }, 'Não desligou')}
                    className={`${BTN_PRIMARIO} flex h-[54px] flex-col items-center justify-center gap-1 text-[11px]`}>
                    <PhoneOff className="h-4 w-4" />Desligar
                  </button>
                </div>
              )}

              <div className="pt-1 text-[9.5px] font-bold uppercase tracking-[1.2px] text-muted-foreground">Quem atendeu?</div>
              <div className="grid grid-cols-2 gap-1.5">
                <button type="button" onClick={() => { setContatoDe(atual); setNome(''); setCargo(''); }} className={BTN_BORDO}>É o contato real</button>
                <button type="button" disabled={ocupado} onClick={() => agir(() => naoEDaquiAutoDialer(atual.id), 'Não marcou')} className={`${BTN} h-9`}>Não é daqui</button>
              </div>
              {s.motivo === 'pausar_ao_terminar' && <p className="text-[11px] text-[#7A2235] dark:text-[#C87484]">Pausa quando esta ligação terminar.</p>}
            </div>
          )}

          {st === 'esperando' && (
            <div className="space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-[1.3px] text-muted-foreground">Ligação encerrada</div>
              <div className="text-[13.5px] font-bold">{atual ? `Você falou no ${fmt(atual.telefone)}` : 'Conversa encerrada'}</div>
              <p className="text-[11px] leading-snug text-muted-foreground">O Auto Dialer está esperando você: depois de uma conversa ele não liga para mais ninguém sozinho.</p>
              {atual && <button type="button" onClick={() => { setContatoDe(atual); setNome(''); setCargo(''); }} className={`${BTN_BORDO} w-full`}>É o contato real</button>}
              <button type="button" disabled={ocupado} onClick={() => agir(proximoLeadAutoDialer, 'Não seguiu')} className={`${BTN_PRIMARIO} w-full`}>
                {proximo ? `Próximo lead · ${proximo.razao_social ?? ''}` : 'Terminar — era o último lead'}
              </button>
              {naFila > 0 && (
                <button type="button" disabled={ocupado} onClick={() => agir(proximoNumeroAutoDialer, 'Não seguiu')} className={`${BTN} h-9 w-full`}>Ligar o próximo número deste lead</button>
              )}
            </div>
          )}

          {st === 'pausado' && (
            <div className="space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-[1.3px] text-[#7A2235] dark:text-[#C87484]">Pausado</div>
              <div className="text-[13.5px] font-bold">{s.motivo === 'sala_fechada' ? 'A sala fechou' : 'Ninguém está sendo chamado'}</div>
              <p className="text-[11px] text-muted-foreground">{s.motivo === 'sala_fechada' ? 'O softphone desligou a sala. Retomar abre a sala de novo e continua de onde parou.' : 'Retomar continua de onde parou.'}</p>
              <button type="button" disabled={ocupado} onClick={() => agir(despausarAutoDialer, 'Não retomou')} className={`${BTN_PRIMARIO} w-full`}>Retomar</button>
            </div>
          )}

          {st === 'encerrada' && (
            <div className="space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-[1.3px] text-muted-foreground">Discagem encerrada</div>
              <div className="text-[13.5px] font-bold">{estado.totais.feitos} de {estado.totais.leads} leads trabalhados</div>
              <p className="text-[11.5px] text-muted-foreground">{estado.totais.falou} com conversa · {estado.totais.sem_contato} sem contato</p>
              {estado.lista && (
                <>
                  <p className="text-[11px] leading-snug text-muted-foreground">A lista <b className="text-foreground">{estado.lista.nome}</b> fica em Minhas listas, na página de Leads.</p>
                  <button type="button" disabled={retomar.isPending} onClick={() => retomar.mutate(estado.lista!.id)} className={`${BTN_BORDO} w-full`}>Retomar esta lista</button>
                </>
              )}
              <button type="button" onClick={onFechar} className={`${BTN} h-9 w-full`}>Fechar</button>
            </div>
          )}

          {(st === 'esperando' || st === 'procurando' || st === 'pausado') && !contatoDe && (
            outroAberto ? (
              <FocoProprio titulo="Ligar para outro número">
                <form
                  className="mt-2 space-y-2 rounded-xl border border-[#5B0A0D] bg-card p-3 dark:border-[#C87484]"
                  onSubmit={(e) => {
                    e.preventDefault();
                    agir(async () => {
                      const r = await ligarOutroNumeroAutoDialer(outroTel);
                      toast.success(`Ligando para ${fmt(r.telefone)} pela Linx`, { description: 'Salvo no lead como "Passado pelo cliente".' });
                      setOutroAberto(false); setOutroTel('');
                    }, 'Não ligou');
                  }}
                >
                  <div className="text-[9.5px] font-bold uppercase tracking-[1.2px] text-[#5B0A0D] dark:text-[#C87484]">Ligar para outro número</div>
                  <input autoFocus inputMode="tel" value={outroTel} onChange={(e) => setOutroTel(e.target.value)} placeholder="DDD + número"
                    className="h-9 w-full rounded-lg border border-border bg-card px-2.5 font-display text-[15px] font-semibold tabular-nums text-foreground" />
                  <p className="text-[10px] leading-snug text-muted-foreground">Liga pela Linx, aqui na sala. O número fica salvo no lead como "Passado pelo cliente". Depois dessa ligação o Auto Dialer espera você.</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button type="button" onClick={() => { setOutroAberto(false); setOutroTel(''); }} className={`${BTN} h-9`}>Cancelar</button>
                    <button type="submit" disabled={ocupado || outroTel.replace(/\D/g, '').length < 10} className={`${BTN_PRIMARIO} h-9`}>Ligar</button>
                  </div>
                </form>
              </FocoProprio>
            ) : (
              <button type="button" onClick={() => setOutroAberto(true)} className={`${BTN} mt-2 h-9 w-full`}>Ligar para outro número</button>
            )
          )}

          {contatoDe && st !== 'encerrada' && (
            <FocoProprio titulo="Salvar como contato real">
            <div className="mt-2 space-y-2 rounded-xl border border-[#5B0A0D] bg-card p-3 dark:border-[#C87484]">
              <div className="text-[9.5px] font-bold uppercase tracking-[1.2px] text-[#5B0A0D] dark:text-[#C87484]">Salvar como contato real · {fmt(contatoDe.telefone)}</div>
              <label className="block text-[10.5px] text-muted-foreground">Nome
                <input autoFocus value={nome} onChange={(e) => setNome(e.target.value)} className="mt-0.5 h-8 w-full rounded-lg border border-border bg-card px-2.5 text-[12.5px] font-semibold text-foreground" />
              </label>
              <label className="block text-[10.5px] text-muted-foreground">Cargo
                <input value={cargo} onChange={(e) => setCargo(e.target.value)} placeholder="ex.: sócio, decide a conta" className="mt-0.5 h-8 w-full rounded-lg border border-border bg-card px-2.5 text-[12.5px] font-semibold text-foreground" />
              </label>
              <div className="grid grid-cols-2 gap-1.5">
                <button type="button" onClick={() => setContatoDe(null)} className={`${BTN} h-9`}>Cancelar</button>
                <button type="button" disabled={ocupado || !nome.trim()}
                  onClick={() => agir(async () => { await contatoRealAutoDialer(contatoDe.id, nome, cargo || null); toast.success('Contato real salvo no lead'); setContatoDe(null); }, 'Não salvou')}
                  className={`${BTN_PRIMARIO} h-9`}>Salvar</button>
              </div>
              {st === 'falando' && <p className="text-[10px] text-muted-foreground">A ligação continua.</p>}
            </div>
            </FocoProprio>
          )}
        </div>

        {/* os números deste lead */}
        {numeros.length > 0 && st !== 'encerrada' && (
          <div>
            <div className="px-3.5 pb-1 pt-2 text-[9.5px] font-bold uppercase tracking-[1.2px] text-muted-foreground">Números deste lead</div>
            {numeros.map((n) => (
              <div key={n.id} className={`grid h-[30px] grid-cols-[8px_minmax(0,1fr)_40px_86px] items-center gap-2 border-t border-[#EDE8E0] px-3.5 dark:border-[#332727] ${n.id === atual?.id ? 'bg-[#F4F0EA] dark:bg-[#2A1F1F]' : ''}`}>
                <span aria-hidden className={`h-2 w-2 rounded-full ${corDoPonto(n.status)}`} />
                <span className="truncate text-[11.5px] tabular-nums" title={n.rotulo || ''}>{fmt(n.telefone)}{n.rotulo ? ` · ${n.rotulo}` : ''}</span>
                <span className="text-[10px] font-bold text-muted-foreground">{operadora(n.operadora) ?? ''}</span>
                <span className={`whitespace-nowrap text-right text-[10px] font-semibold ${n.status === 'atendeu' ? 'text-[#075E54] dark:text-[#25D366]' : n.status === 'discando' ? 'text-[#5B0A0D] dark:text-[#C87484]' : 'text-muted-foreground'}`}>{ROTULO_STATUS[n.status]}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {estado.placar_hoje && estado.placar_hoje.ligacoes > 0 && (
        <div className="flex-none border-t border-border px-3.5 py-1.5 text-[10.5px] tabular-nums text-muted-foreground">
          Hoje: <b className="text-foreground">{estado.placar_hoje.ligacoes}</b> {estado.placar_hoje.ligacoes === 1 ? 'ligação' : 'ligações'} · {estado.placar_hoje.atenderam} {estado.placar_hoje.atenderam === 1 ? 'atendeu' : 'atenderam'} · {estado.placar_hoje.caixa_postal} caixa postal · {estado.placar_hoje.leads} {estado.placar_hoje.leads === 1 ? 'lead' : 'leads'}
        </div>
      )}

      {/* a discagem */}
      {st !== 'encerrada' && (
        <div className="grid flex-none grid-cols-3 gap-1.5 border-t border-border bg-[#FAF8F4] px-3.5 py-2.5 dark:bg-[#241A1A]">
          {st === 'pausado' ? (
            <button type="button" disabled={ocupado} onClick={() => agir(despausarAutoDialer, 'Não retomou')} className={BTN}>Retomar</button>
          ) : st === 'falando' && s.motivo === 'pausar_ao_terminar' ? (
            <button type="button" disabled={ocupado} onClick={() => agir(despausarAutoDialer, 'Não mudou')} className={BTN}>Não pausar</button>
          ) : (
            <button type="button" disabled={ocupado} onClick={() => agir(pausarAutoDialer, 'Não pausou')} className={BTN}>Pausar</button>
          )}
          <button type="button" disabled={ocupado || st === 'falando'} onClick={() => agir(proximoLeadAutoDialer, 'Não pulou')} className={BTN} title={st === 'falando' ? 'Desligue a ligação antes' : 'Deixa os números que faltam e vai para o próximo lead'}>Pular lead</button>
          <button type="button" disabled={ocupado}
            onClick={() => {
              if (st === 'falando' && !window.confirm('Parar a discagem desliga a ligação de agora. A lista fica em Minhas listas. Parar?')) return;
              agir(encerrarAutoDialer, 'Não encerrou');
            }}
            className={`${BTN} text-[#7A2235] dark:text-[#C87484]`} title="A lista fica em Minhas listas e você retoma de onde parou">Parar</button>
        </div>
      )}
    </div>
  );
}
